import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { featureDetails } from './AIFeatureDetailPage';
import DemoBookingModal from '../components/DemoBookingModal';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicFeaturesListPage() {
  const { language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchParams] = useSearchParams();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // UTM tracking
  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    const utmCampaign = searchParams.get('utm_campaign');
    if (utmSource || utmCampaign) {
      const utmData = {
        utm_source: utmSource || '',
        utm_medium: searchParams.get('utm_medium') || '',
        utm_campaign: utmCampaign || '',
        utm_term: searchParams.get('utm_term') || '',
        utm_content: searchParams.get('utm_content') || '',
        landing_url: window.location.href,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('shiksha_utm', JSON.stringify(utmData));
      fetch(`${API_URL}/api/campaign/track-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(utmData)
      }).catch(() => {});
    }
  }, [searchParams]);

  const featureList = Object.entries(featureDetails).map(([id, f]) => ({
    id,
    name: f.name[language],
    icon: f.icon,
    desc: f.shortDesc[language],
    category: f.category,
  }));

  const categories = [
    { id: 'all', label: language === 'hi' ? 'सभी' : 'All' },
    { id: 'admin', label: language === 'hi' ? 'एडमिन' : 'Admin' },
    { id: 'teacher', label: language === 'hi' ? 'शिक्षक' : 'Teacher' },
    { id: 'student', label: language === 'hi' ? 'विद्यार्थी' : 'Student' },
    { id: 'parent', label: language === 'hi' ? 'अभिभावक' : 'Parent' },
    { id: 'all_users', label: language === 'hi' ? 'सभी उपयोगकर्ता' : 'All Users' },
  ];

  const filtered = activeCategory === 'all'
    ? featureList
    : activeCategory === 'all_users'
      ? featureList.filter(f => f.category === 'all')
      : featureList.filter(f => f.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="public-features-list-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="text-2xl font-bold text-orange-600">शिक्षा मित्र</span>
            <span className="text-xs text-gray-400 hidden sm:inline">by System Of Success</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-gray-600 hover:text-orange-600 transition hidden sm:inline">
              {language === 'hi' ? 'होम' : 'Home'}
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition"
              data-testid="header-login-btn"
            >
              {language === 'hi' ? 'लॉगिन' : 'Login'}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800" data-testid="features-page-title">
            {language === 'hi' ? 'AI Features & School Modules' : 'AI Features & School Modules'}
          </h1>
          <p className="text-gray-500 mt-2 max-w-xl mx-auto">
            {language === 'hi'
              ? 'शिक्षा मित्र के सभी AI-powered features जो आपके स्कूल को smart बनाएंगे।'
              : 'All AI-powered features of Shiksha Mitra that will make your school smart.'}
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                activeCategory === cat.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
              <span className="ml-1 text-xs opacity-70">
                ({cat.id === 'all' ? featureList.length :
                  cat.id === 'all_users' ? featureList.filter(f => f.category === 'all').length :
                  featureList.filter(f => f.category === cat.id).length})
              </span>
            </button>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(f => (
            <Link
              key={f.id}
              to={`/feature/${f.id}`}
              className="group bg-white rounded-xl p-5 shadow-sm hover:shadow-md border border-transparent hover:border-orange-200 transition-all"
              data-testid={`feature-card-${f.id}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl">{f.icon}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition">
                    {f.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{f.desc}</p>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${
                    f.category === 'admin' ? 'bg-purple-50 text-purple-600' :
                    f.category === 'teacher' ? 'bg-blue-50 text-blue-600' :
                    f.category === 'student' ? 'bg-green-50 text-green-600' :
                    f.category === 'parent' ? 'bg-orange-50 text-orange-600' :
                    'bg-gray-50 text-gray-600'
                  }`}>
                    {f.category === 'admin' ? (language === 'hi' ? 'एडमिन' : 'Admin') :
                     f.category === 'teacher' ? (language === 'hi' ? 'शिक्षक' : 'Teacher') :
                     f.category === 'student' ? (language === 'hi' ? 'विद्यार्थी' : 'Student') :
                     f.category === 'parent' ? (language === 'hi' ? 'अभिभावक' : 'Parent') :
                     (language === 'hi' ? 'सभी' : 'All')}
                  </span>
                </div>
                <svg className="w-5 h-5 text-gray-300 group-hover:text-orange-400 transition shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">
            {language === 'hi' ? 'अपने स्कूल को AI-powered बनाएं!' : 'Make your school AI-powered!'}
          </h2>
          <p className="text-orange-100 mb-4">
            {language === 'hi'
              ? '31+ AI features, Padhai EMI, Admission Engine और बहुत कुछ।'
              : '31+ AI features, Padhai EMI, Admission Engine and much more.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => setShowDemoModal(true)} className="px-6 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition" data-testid="cta-home">
              {language === 'hi' ? 'Free Demo' : 'Free Demo'}
            </button>
            <button onClick={() => setShowPaymentModal(true)} className="px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition" data-testid="cta-payment">
              {language === 'hi' ? 'Payment से Start करें' : 'Start with Payment'}
            </button>
            <Link to="/school-match" className="px-6 py-3 bg-orange-700 text-white font-semibold rounded-lg hover:bg-orange-800 transition" data-testid="cta-school-match">
              {language === 'hi' ? 'स्कूल खोजें' : 'Find School'}
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-auto">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">© 2025 System Of Success. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/contact" className="hover:text-white transition">Contact</Link>
          </div>
        </div>
      </footer>

      <DemoBookingModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} source="public_features_list" />
      <SubscriptionPaymentModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} language={language} startStep={1} source="public_features_list_payment" />
    </div>
  );
}
